import { Grid } from '@mui/material';
import { Language, LanguageType } from '@nx-smartmonkey/shared/domain';
import { getMonth, getYear, getYearMonthDay } from '@nx-smartmonkey/shared/helpers';
import { RoutalDropdownIcon, RoutalInputDatePicker, RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { createViewMonthGrid } from '@schedule-x/calendar';
import { createCalendarControlsPlugin } from '@schedule-x/calendar-controls';
import { createEventModalPlugin } from '@schedule-x/event-modal';
import { createEventsServicePlugin } from '@schedule-x/events-service';
import { ScheduleXCalendar, useCalendarApp } from '@schedule-x/react';
import '@schedule-x/theme-default/dist/index.css';
import moment from 'moment';
import { CSSProperties } from 'preact/compat';
import { useEffect, useMemo, useState } from 'react';

import { DateRange } from '.';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { CreatePlanModal } from '../create-plan/CreatePlanModal';
import { useManagePlanQuery } from '../hooks/useManagePlanQuery';
import { useRetrieveWeatherForecast } from '../hooks/useRetrieveWeatherForecast';
import { useSetSelectedDay } from '../hooks/useSetSelectedDay';
import './CalendarStyle.css';
import { CalendarEvent } from './components/CalendarEvent';
import { EventModal } from './components/EventModal';
import { PlanCalendarEventMapper, PlanCalendarEventProps } from './PlanCalendarEventMapper';
import { PlansSearcher } from './PlansSearcher';
import { addWeatherToDate } from './Weather';

const dayToIntl: { [key: number]: string } = {
  0: `Sunday.short`,
  1: `Monday.short`,
  2: `Tuesday.short`,
  3: `Wednesday.short`,
  4: `Thursday.short`,
  5: `Friday.short`,
  6: `Saturday.short`,
};

// The library available locales: https://schedule-x.dev/docs/calendar/supported-languages
const languageToLocale = (language: LanguageType) => {
  switch (language) {
    case `es`:
      return `es-ES`;
    case `pt`:
      return `pt-BR`;
    case `ca`:
      return `ca-ES`;
    case `de`:
      return `de-DE`;
    case `fr`:
      return `fr-FR`;
    case `ru`:
      return `ru-RU`;
    default:
      return `en-US`;
  }
};

const SelectedDayStyle: CSSProperties = {
  border: `2px solid ${RoutalPalette.primary40}`,
  borderRadius: `10px`,
  inset: `-2px`,
  width: `auto`,
  height: `auto`,
};

interface PlannerPlansCalendarProps {
  plans: PlanFlat[];
  isLoading: boolean;
  setDateRange: (dateRange: DateRange) => void;
}

export const PlannerPlansCalendar = ({ plans, isLoading, setDateRange }: PlannerPlansCalendarProps) => {
  const { data: user } = useRetrieveUser();
  const { data: weatherForecast } = useRetrieveWeatherForecast();
  const language = user?.language.value ?? Language.getDefaultLanguage().value;
  const { selectedDay, setSelectedDay } = useSetSelectedDay();
  const { navigateToPlan } = useManagePlanQuery();

  const [createPlanDialog, setCreatePlanDialog] = useState<boolean>(false);
  const [renderWeather, setRenderWeather] = useState<boolean>(false);
  const [showCalendar, setShowCalendar] = useState<boolean>(false);

  // This is kept, mostly, to prevent an error witth the plugins when hot-reloading.
  // When it re-renders the component, it creates again the plugins and the app has to be assigned again.
  const [app, setApp] = useState<any | null>(null);

  const amountOfEventsPerDay = () => {
    if (window) {
      const innerHeight = window.innerHeight;
      // The values have been manually calculated. If you change them, make sure they perfectly fit with the amount of events.
      if (innerHeight > 997) {
        return 3;
      }
      if (innerHeight > 857) {
        return 2;
      }
      return 1;
    }
    return 1;
  };

  // The week days correspond to the following:
  // 0: Sunday
  // 1: Monday
  // ...
  const weekDays = (firstDay: number) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(dayToIntl[(firstDay + i) % 7]);
    }
    return days;
  };

  // Aux functions
  const changeSelectedDay = (date: string) => {
    setSelectedDay(moment(date, `YYYY-MM-DD`).utc().toDate());
    eventsServicePlugin.setBackgroundEvents([
      {
        start: date,
        end: date,
        style: SelectedDayStyle,
      },
    ]);
  };

  const changeDateRange = (from: string, to: string) => {
    setDateRange({
      startDate: from,
      endDate: to,
    });
  };

  const renderWeatherIcons = () => {
    if (!calendarControls.$app || !weatherForecast) return;

    weatherForecast.forEach((weather) => {
      addWeatherToDate(weather);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const handleClick = (direction: `next` | `prev`) => {
    const date = calendarControls.getDate();

    if (!date) return;

    const selectedDate = moment(new Date(date));

    const newDate = direction === `next` ? selectedDate.add(1, `month`) : selectedDate.subtract(1, `month`);

    calendarControls.setDate(getYearMonthDay({ date: newDate.toDate() }));
  };

  // Calendar package plugins
  const eventsServicePlugin = useMemo(() => createEventsServicePlugin(), []);
  const eventModal = useMemo(() => createEventModalPlugin(), []);
  const calendarControls = useMemo(() => createCalendarControlsPlugin(), []);

  useEffect(() => {
    if (!calendarControls.$app) return;

    const selectedDayString = getYearMonthDay({ date: selectedDay ?? new Date() });
    if (calendarControls.getDate() !== selectedDayString) {
      calendarControls.setDate(selectedDayString);
      changeSelectedDay(selectedDayString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  useEffect(() => {
    calendarControls.beforeRender(app);
    eventsServicePlugin.beforeRender(app);
    if (eventModal.beforeRender) eventModal.beforeRender(app);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarControls, eventsServicePlugin, eventModal, app]);

  useEffect(() => {
    // Check if the current calendar events are different from the plans. If so, update the calendar events
    // This is done in order to avoid re-rendering the calendar every time the plans
    const currentEvents = calendar.events.getAll();
    const currentEventsIds = currentEvents.map((event) => event.id);

    const newEvents = plans.map((plan) => PlanCalendarEventMapper.toCalendarEvent(plan));
    const newEventsIds = newEvents.map((event) => event.id);

    if (JSON.stringify(currentEventsIds) !== JSON.stringify(newEventsIds)) {
      calendar.events.set(newEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  useEffect(() => {
    if (renderWeather) {
      renderWeatherIcons();
      setRenderWeather(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderWeather]);

  const calendar = useCalendarApp({
    isResponsive: true,
    views: [createViewMonthGrid()],
    skipValidation: true,
    locale: languageToLocale(language),
    callbacks: {
      onClickDate: (date) => {
        // This is added to highlight the selected date eventhough it's not the library selected-day
        if (calendarControls.getDate() === date) {
          setCreatePlanDialog(true);
        } else {
          changeSelectedDay(date);
        }
      },
      onDoubleClickDate: (date) => {
        setCreatePlanDialog(true);
      },
      onDoubleClickEvent: (event, e) => {
        navigateToPlan({ planId: event.id.toString() });
      },
      onClickPlusEvents: (date) => {
        changeSelectedDay(date);
      },
      onRangeUpdate: (range) => {
        changeDateRange(range.start, range.end);
        setTimeout(() => {
          setRenderWeather(true);
        }, 400);
      },
      onSelectedDateUpdate: () => {
        eventModal.close();
      },
      beforeRender: ($app) => {
        setApp($app);
      },
      onRender: ($app) => {
        setTimeout(() => {
          setRenderWeather(true);
        }, 400);
        const dateRange = calendarControls.getRange();
        if (dateRange) changeDateRange(dateRange?.start, dateRange?.end);

        const date = calendarControls.getDate();
        eventsServicePlugin.setBackgroundEvents([
          {
            start: date,
            end: date,
            style: SelectedDayStyle,
          },
        ]);
      },
    },
    monthGridOptions: {
      nEventsPerDay: amountOfEventsPerDay(),
    },
    plugins: [calendarControls, eventsServicePlugin, eventModal],
  });

  const memoizedCalendarComponent = useMemo(() => {
    return (
      <ScheduleXCalendar
        calendarApp={calendar}
        customComponents={{
          headerContent: () => {
            return null;
          },
          eventModal: ({ calendarEvent }: { calendarEvent: PlanCalendarEventProps }) => {
            return <EventModal calendarEvent={calendarEvent} />;
          },
          monthGridEvent: ({ calendarEvent }: { calendarEvent: PlanCalendarEventProps }) => {
            return <CalendarEvent calendarEvent={calendarEvent} />;
          },
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ height: `100%`, width: `100%`, position: `relative` }}>
      {calendarControls.$app && (
        <Grid container direction="column">
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{ height: `50px`, padding: `0 5px` }}
          >
            <Grid container direction="row" alignItems="center" gap="8px" style={{ width: `unset` }}>
              <Button
                variant="flat"
                styleType="secondary"
                onClick={() => {
                  const todayDate = getYearMonthDay({ date: new Date() });
                  calendarControls.setDate(todayDate);
                  changeSelectedDay(todayDate);
                }}
                intlId="planner.plans.calendar.today"
              />
              <Button onClick={() => handleClick(`prev`)} style={{ padding: `8px`, backgroundColor: `transparent` }}>
                <RoutalDropdownIcon style={{ color: RoutalPalette.secondary40, transform: `rotate(90deg)` }} />
              </Button>
              <Button onClick={() => handleClick(`next`)} style={{ padding: `8px`, backgroundColor: `transparent` }}>
                <RoutalDropdownIcon style={{ color: RoutalPalette.secondary40, transform: `rotate(-90deg)` }} />
              </Button>
              <Grid display="flex" position="relative">
                {showCalendar && (
                  <div style={{ position: `absolute`, top: `30px`, zIndex: `9999`, width: `220px` }}>
                    <RoutalInputDatePicker
                      portalId="root-datepicker"
                      showMonthYearPicker
                      inline
                      onChange={(date) => {
                        calendarControls.setDate(getYearMonthDay({ date }));
                        setShowCalendar(false);
                      }}
                      onClose={() => setShowCalendar(false)}
                      dateValue={new Date(calendarControls.getDate())}
                    />
                  </div>
                )}
                <Text
                  variant="h6"
                  style={{ textTransform: `capitalize`, cursor: `pointer` }}
                  onClick={() => {
                    setShowCalendar(true);
                  }}
                >{`${getMonth({
                  date: new Date(calendarControls.getDate()),
                  language,
                  large: true,
                })} ${getYear({
                  date: new Date(calendarControls.getDate()),
                  language,
                })}`}</Text>
              </Grid>
            </Grid>

            <PlansSearcher />
          </Grid>

          <VSpacer medium />

          <Grid container direction="row" width="100%">
            {weekDays(calendarControls.$app.config.firstDayOfWeek.value).map((weekDay) => (
              <Grid item width="calc(100%/7)" textAlign="center">
                <Text intlId={weekDay} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      {isLoading && (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          wrap="nowrap"
          style={{
            position: `absolute`,
            width: `200px`,
            height: `100px`,
            top: `calc(50% - 50px)`,
            left: `calc(50% - 100px)`,
            zIndex: 10,
          }}
        >
          <RoutalLoader width={30} style={{ height: `50px` }} />

          <Text
            variant="body-large"
            isBold
            style={{
              color: RoutalPalette.primary40,
              marginTop: `-22px`,
            }}
            intlId="planner.plans.calendar.loading_plans"
          />
        </Grid>
      )}
      {createPlanDialog && (
        <CreatePlanModal
          open={createPlanDialog}
          selectedDay={selectedDay}
          onCloseDialog={() => setCreatePlanDialog(false)}
          handleAfterCreatePlan={(planId) => {
            navigateToPlan({ planId, invalidateQueries: true });
            setCreatePlanDialog(false);
          }}
        />
      )}
      <div style={{ width: `100%`, height: `calc(100% - 82px)` }}>{memoizedCalendarComponent}</div>
    </div>
  );
};
